import { useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useActiveAnnouncements, useAnnouncementImage, useDismissAnnouncement } from '../../hooks/useAnnouncements';

/**
 * Mounted once in AppLayout. Shows the oldest-pending active announcement the current
 * user hasn't dismissed, one at a time. Two ways to close:
 *   - "Don't notify me again" — permanent, calls the dismiss endpoint.
 *   - × — this session only; `hiddenIds` is purely local state, so it reappears next time
 *     the app loads (the active-announcements query still returns it).
 * Either way hides it locally right away instead of waiting on the next refetch.
 */
export function AnnouncementModal() {
  const { data: announcements } = useActiveAnnouncements();
  const dismiss = useDismissAnnouncement();
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  const current = useMemo(
    () => announcements?.find((a) => !hiddenIds.has(a.id)),
    [announcements, hiddenIds],
  );
  const imageUrl = useAnnouncementImage(current?.id);

  function hideLocally(id: string) {
    setHiddenIds((prev) => new Set(prev).add(id));
  }

  function handleCloseForSession() {
    if (current) hideLocally(current.id);
  }

  function handleDismissPermanently() {
    if (!current) return;
    hideLocally(current.id);
    dismiss.mutate(current.id);
  }

  if (!current) return null;

  return (
    <Dialog open onClose={handleCloseForSession} maxWidth="sm" fullWidth>
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleCloseForSession}
          aria-label="Close"
          sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper', zIndex: 1 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          {imageUrl && <img src={imageUrl} alt={current.title} style={{ width: '100%', display: 'block' }} />}
        </DialogContent>
      </Box>
      <DialogActions>
        <Button onClick={handleDismissPermanently} color="inherit">
          ไม่ต้องแจ้งเตือนอีก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
